import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    url: "https://www.facebook.com/share/g/17wjdt4tzJ/?mibextid=wwXIfr",
    color: "#1877F2",
    memberOnly: true,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    ),
  },
  {
    label: "Discord",
    url: "https://discord.gg/fjfXfa3AH5",
    color: "#5865F2",
    memberOnly: true,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg>
    ),
  },
  {
    label: "YouTube",
    url: "https://youtube.com/@official_sagt?si=2YYn4f3QTBuPxSzN",
    color: "#FF0000",
    memberOnly: false,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
    ),
  },
];

// Members-only community links are gated the same way as the Home page
// "Join the Community" section — signed-out visitors see a lock icon and a
// tooltip nudging them to sign in. Signed-in members click through normally.
function SocialIcon({ social, isMember }) {
  const gated = social.memberOnly && !isMember;
  if (gated) {
    return (
      <Link
        to="/member/login"
        style={{ "--brand": social.color }}
        className="brand-social-btn w-10 h-10 flex items-center justify-center border rounded-md transition-all relative opacity-50 hover:opacity-100"
        title={`${social.label} — members only. Sign in to access.`}
        data-testid={`footer-social-${social.label.toLowerCase()}`}
        aria-label={`${social.label} (members only)`}
      >
        {social.icon}
        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-black border border-white/20 flex items-center justify-center">
          <Lock className="w-2 h-2 text-white/70" />
        </span>
      </Link>
    );
  }
  return (
    <a
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ "--brand": social.color }}
      className="brand-social-btn w-10 h-10 flex items-center justify-center border rounded-md transition-all"
      title={social.label}
      data-testid={`footer-social-${social.label.toLowerCase()}`}
    >
      {social.icon}
    </a>
  );
}

export default function Footer() {
  // Cheap client-side signed-in check — the community links are already
  // guarded server-side inside the Members Discord / Facebook group, so
  // this is purely a UX cue.
  const isMember = typeof window !== "undefined" && !!localStorage.getItem("sagt_member_token");
  return (
    <footer className="border-t border-white/5 bg-black/40 backdrop-blur-sm" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Quick Links — split into 2 sub-columns so it scales as we add pages */}
          <div>
            <h4 className="font-heading text-xs uppercase tracking-widest text-[#FFB81C] mb-4">Quick Links</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { label: "Home", to: "/home" },
                { label: "About Us", to: "/about" },
                { label: "Daily Races", to: "/races" },
                { label: "Leaderboard", to: "/leaderboard" },
                { label: "Rankings", to: "/rankings" },
                { label: "Events", to: "/events" },
                { label: "SAGT Cup", to: "/sagt-cup" },
                { label: "SAGT Academy", to: "/academy" },
                { label: "Leagues", to: "/leagues/login" },
                { label: "Register", to: "/register" },
                { label: "Sign In", to: "/login" },
                { label: "FAQs", to: "/faq" },
                { label: "Privacy Policy", to: "/privacy" },
                { label: "Coach Login", to: "/coach/login" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="block font-body text-sm text-white/40 hover:text-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact & Merch */}
          <div>
            <h4 className="font-heading text-xs uppercase tracking-widest text-[#FFB81C] mb-4">Contact Us</h4>
            <div className="space-y-1">
              <a href="mailto:support@sagtonline.co.za" className="block font-body text-sm text-white/60 hover:text-[#FFB81C] transition-colors break-all" data-testid="footer-email">
                support@sagtonline.co.za
              </a>
              <a href="mailto:coaching@sagtonline.co.za" className="block font-body text-sm text-white/60 hover:text-[#FFB81C] transition-colors break-all" data-testid="footer-email-coaching">
                coaching@sagtonline.co.za
              </a>
            </div>
            <div className="mt-6">
              <h4 className="font-heading text-xs uppercase tracking-widest text-white/30 mb-2">Merchandise Shop</h4>
              <p className="font-body text-sm text-white/40">Currently in the preparation phase. Stay tuned for updates.</p>
            </div>
          </div>

          {/* Socials */}
          <div>
            <h4 className="font-heading text-xs uppercase tracking-widest text-[#FFB81C] mb-4">Follow SAGT</h4>
            <div className="flex gap-4">
              {SOCIAL_LINKS.map((s) => (
                <SocialIcon key={s.label} social={s} isMember={isMember} />
              ))}
            </div>
            {!isMember && (
              <p className="font-body text-[10px] text-white/30 mt-3" data-testid="footer-socials-members-hint">
                <Lock className="w-2.5 h-2.5 inline mr-1" />
                Facebook & Discord are members-only. <Link to="/member/login" className="text-[#FFB81C] hover:text-white transition-colors">Sign in</Link> to open them.
              </p>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-white/5 pt-6" data-testid="footer-disclaimer">
          <p className="font-body text-xs text-white/30 leading-relaxed">
            <span className="text-white/50 font-medium">Disclaimer:</span> SAGT is an independent Gran Turismo fan community created specifically for South African players. We are not affiliated with Polyphony Digital or any other leagues and organisations. Membership is built on respect, competitiveness and sportsmanship. All members agree to community rules to maintain a focused and positive environment.
          </p>
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="font-body text-xs text-white/20">
              &copy; {new Date().getFullYear()} SAGT. All rights reserved.
            </p>
            <Link
              to="/admin"
              className="font-heading text-[10px] uppercase tracking-wider text-white/15 hover:text-white/40 transition-colors"
              data-testid="footer-link-admin"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
